import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouterLegacy } from 'core/routerUtils';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { find, isEmpty, isObject } from 'lodash';
import * as irregularitiesActions from 'features/irregularities/duck';
import * as roomActions from 'features/room-builder/duck';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as taskActions from 'features/task-management/duck';
import * as materialChainActions from 'features/material-chain/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import { convertKeywords, isRoomOnline, hasRoomMaterialsRequired } from 'shared/helpers';
import { IrregularityModel } from 'features/irregularities';
import { ROOM_STATUS } from 'shared/constants';
import { Textarea, AlertNotice, Spinner, SimpleModal } from 'shared/components';
import {
  getRoomAvailableTasks,
  getCompletedTasks,
  mapDisplayTexts,
} from 'features/task-management/selectors';
import { PermissionsService } from 'shared/services';

import './CloseRoom.scss';

const IRREGULARITY_MAX_LENGTH = 5000;

const findRoomDTO = (listOfRoomsDTO, roomId) => listOfRoomsDTO.find((r) => r.id == roomId) || {};

class CloseRoom extends Component {
  constructor() {
    super();

    this.state = {
      errors: {},
      irregularityDescription: '',
      room: {},
      discardModalOpen: false,
      isDirty: false,
    };

    this.goBack = this.goBack.bind(this);
    this.closeDiscardModal = this.closeDiscardModal.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
    this.onChangeIrregularityDescription = this.onChangeIrregularityDescription.bind(this);
    this.submitIrreg = this.submitIrreg.bind(this);
  }

  componentDidMount() {
    const { roomId, eventId } = this.props.match.params;
    const { actions, tcEvent, displayTexts, match } = this.props;

    actions.getRoomStatus(roomId);
    actions.getRoom(roomId);

    if (isEmpty(tcEvent)) {
      actions.getTcEvent(match.params.eventId).then(() => {
        if (!isEmpty(this.props.tcEvent)) {
          actions.getTasks(this.props.tcEvent.testingEvent.adminId.cycleId.id);
        }
        const cycleId = this.props.tcEvent.testingEvent.adminId.cycleId.id;
        actions.getIrregularityTypes({ cycleId: cycleId });
        if (
          isEmpty(displayTexts) ||
          !displayTexts.room_tips_for_success.cycle ||
          displayTexts.room_tips_for_success.cycle.id !== cycleId
        ) {
          actions.getDisplayTexts(cycleId);
        }
        if (
          PermissionsService.hasTestCenterManage(this.props.tcEvent.testCenterCycleId) ||
          PermissionsService.hasEventManage(eventId) ||
          PermissionsService.hasRoomAccess(eventId)
        ) {
          actions.getRoomsDTO(eventId);
        } else {
          actions.getRoomDTO(roomId);
        }
      });
    } else {
      const cycleId = tcEvent.testingEvent.adminId.cycleId.id;
      actions.getTasks(cycleId);
      actions.getIrregularityTypes({ cycleId: cycleId });
      if (
        isEmpty(displayTexts) ||
        !displayTexts.room_tips_for_success.cycle ||
        displayTexts.room_tips_for_success.cycle.id !== cycleId
      ) {
        actions.getDisplayTexts(cycleId);
      }
      if (
        PermissionsService.hasTestCenterManage(tcEvent.testCenterCycleId) ||
        PermissionsService.hasEventManage(eventId) ||
        PermissionsService.hasRoomAccess(eventId)
      ) {
        actions.getRoomsDTO(eventId);
      } else {
        actions.getRoomDTO(roomId);
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { roomDTO } = this.state;

    if (
      this.props.roomsDTO.length &&
      (isEmpty(roomDTO) || roomDTO.id !== +this.props.match.params.roomId) &&
      prevProps.roomsDTO !== this.props.roomsDTO
    ) {
      this.setState({
        roomDTO: findRoomDTO(this.props.roomsDTO, this.props.match.params.roomId),
      });
    }

    if (
      !isEmpty(this.props.room) &&
      !this.props.room.loading &&
      isEmpty(this.state.room) &&
      prevProps.room !== this.props.room
    ) {
      this.setState({
        room: {
          ...this.props.room,
        },
      });
    }
  }

  async handleContinue() {
    const { irregularityDescription } = this.state;
    const { actions, completedTasks, roomAvailableTasks, room, roomStatus } = this.props;
    const { roomDTO } = this.state;

    if (
      roomAvailableTasks.length !== completedTasks.length &&
      !roomDTO.manualTimerFlag &&
      roomStatus.status !== ROOM_STATUS.closed
    ) {
      if (isEmpty(irregularityDescription)) {
        this.setState({
          errors: {
            irregularityDescription: ['errors.reasonRequired'],
          },
        });
        return;
      }
    }

    actions.updateRoomStatus(this.props.room.id, { status: ROOM_STATUS.closed }).then(() => {
      const { errorUpdateStatus } = this.props;

      if (!errorUpdateStatus || isEmpty(errorUpdateStatus)) {
        actions.setRoomStatusAction({
          data: {
            status: ROOM_STATUS.closed,
          },
        });
        if (roomAvailableTasks.length !== completedTasks.length && !roomDTO.manualTimerFlag) {
          if (!isEmpty(irregularityDescription)) {
            this.submitIrreg();
          }
        }
        this.props.history.goBack();
        this.props.history.replace(
          `/test-event/${this.props.match.params.eventId}/room/${room.id}`,
          {
            successNotif: 'reviewAndCloseRoom.successMessage',
          }
        );
      } else {
        this.setState({
          serverError: isObject(errorUpdateStatus.error)
            ? errorUpdateStatus.error.message
            : errorUpdateStatus.error,
        });
      }
    });
  }

  async submitIrreg() {
    const { eventId, roomId } = this.props.match.params;
    const { actions, user, irregularityTypes } = this.props;

    const typeId = find(irregularityTypes, {
      type: 'room_closed_in_advance',
    }).id;
    const irregularity = new IrregularityModel({
      testCenterEventId: eventId,
      scope: 'room',
      notes: this.state.irregularityDescription,
      submitPersonnel: { id: user.id },
      irregularityRooms: [{ roomId }],
      irregularityType: {
        id: typeId,
      },
      submitDateTime: new Date(),
      startDateTime: new Date(),
      duration: 1,
    });
    await actions.setIrregularity(irregularity);
  }

  onChangeIrregularityDescription(e) {
    const { value } = e.target;

    this.setState((prevState) => ({
      irregularityDescription: value,
      errors: {
        ...prevState.errors,
        irregularityDescription: '',
      },
      isDirty: true,
    }));
  }

  closeDiscardModal(leavePage) {
    if (leavePage) {
      this.props.history.goBack();
    }
    this.setState({
      discardModalOpen: false,
    });
  }

  goBack() {
    if (this.state.isDirty) {
      this.setState({
        discardModalOpen: true,
      });
      return;
    }
    this.props.history.goBack();
  }

  render() {
    const { roomStatus, completedTasks, roomAvailableTasks, displayTexts, tcEvent } = this.props;

    const {
      irregularityDescription,
      errors,
      roomDTO,
      room,
      serverError,
      errorAnswers,
      discardModalOpen,
    } = this.state;

    if (
      isEmpty(roomDTO) ||
      isEmpty(roomStatus) ||
      roomStatus.loading ||
      isEmpty(displayTexts) ||
      isEmpty(room) ||
      room.loading
    ) {
      return <Spinner />;
    }

    return (
      roomStatus.status !== ROOM_STATUS.inactive && (
        <div className="close-room-container col col-md-8 col-lg-9">
          {isRoomOnline(room.roomType) || !hasRoomMaterialsRequired(room.roomType) ? (
            <h4 className="pt-3">
              {roomAvailableTasks.length !== completedTasks.length
                ? this.props.t('taskManagement.closeRoom.titleCloseInAdvanceOnline')
                : this.props.t('taskManagement.closeRoom.titleCloseOnline')}
            </h4>
          ) : (
            <h4 className="pt-3">
              {roomAvailableTasks.length !== completedTasks.length && !roomDTO.manualTimerFlag
                ? this.props.t('taskManagement.closeRoom.titleCloseInAdvance')
                : this.props.t('taskManagement.closeRoom.titleClose')}
            </h4>
          )}
          <div className="room-name">{room.name}</div>
          <div className="mt-3">
            <AlertNotice
              message={this.props.t(
                tcEvent.specialTestingFlag
                  ? 'taskManagement.closeRoom.infoMessageSpecialTesting'
                  : 'taskManagement.closeRoom.infoMessage'
              )}
            />
          </div>
          <div className="required-actions">
            <div
              dangerouslySetInnerHTML={{
                __html: convertKeywords(displayTexts.room_close_instructions.text),
              }}
            />
          </div>
          {errorAnswers && (
            <div className="mt-3 mb-3">
              <AlertNotice
                message={this.props.t('taskManagement.closeRoom.errorAnswers')}
                type="error"
              />
            </div>
          )}
          {roomAvailableTasks.length !== completedTasks.length &&
            !roomDTO.manualTimerFlag &&
            roomStatus.status !== ROOM_STATUS.closed && (
              <Textarea
                className="mb-1 mt-1"
                label={this.props.t('taskManagement.closeRoom.reasonLabel')}
                name="irregularityDescription"
                value={irregularityDescription}
                onChange={this.onChangeIrregularityDescription}
                maxLength={IRREGULARITY_MAX_LENGTH}
                error={errors.irregularityDescription}
              />
            )}
          {serverError && (
            <div className="mt-3 mb-3">
              <AlertNotice message={serverError} type="error" />
            </div>
          )}
          <div className="buttons-container">
            <button className="btn btn-warn" onClick={this.goBack}>
              {this.props.t('common.labels.back')}
            </button>
            <button className="btn btn-action" onClick={this.handleContinue}>
              <span className="hide-mobile">
                {this.props.t('taskManagement.closeRoom.closeBtn')}
              </span>
              <span className="show-mobile">{this.props.t('common.labels.confirm')}</span>
            </button>
          </div>
          <SimpleModal
            isOpen={discardModalOpen}
            onCancel={() => this.closeDiscardModal(false)}
            onConfirm={() => this.closeDiscardModal(true)}
            confirmLabel={this.props.t('common.labels.yes')}
            cancelLabel={this.props.t('common.labels.no')}
            headline={this.props.t('common.backModal.title')}
            message={this.props.t('common.backModal.discardPageMessage')}
            buttonsReverseOrder
          />
        </div>
      )
    );
  }
}

function mapStateToProps(state) {
  return {
    user: state.users.currentUser || {},
    roomStatus: state.taskManagement.roomStatus,
    room: state.roomOptions.room || {},
    errorUpdateStatus: state.roomOptions.errorUpdateStatus,
    tcEvent: state.tcEvent.currentEvent || {},
    irregularityTypes: state.statics.irregularityTypes || [],
    roomsDTO:
      (!isEmpty(state.tcEvent.roomsDTO) ? state.tcEvent.roomsDTO : state.roomOptions.roomDTO) || [],
    completedTasks: getCompletedTasks(state) || [],
    roomAvailableTasks: getRoomAvailableTasks(state) || [],
    displayTexts: mapDisplayTexts(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...tcEventActions,
        ...irregularitiesActions,
        ...taskActions,
        ...roomActions,
        ...materialChainActions,
        ...staticActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(CloseRoom))
);

CloseRoom.propTypes = {
  t: PropTypes.func.isRequired,
  // Props from react-router (via withRouterLegacy)
  match: PropTypes.shape({
    params: PropTypes.shape({
      roomId: PropTypes.string.isRequired,
      eventId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,

  // Props from mapStateToProps
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  tcEvent: PropTypes.shape({
    testingEvent: PropTypes.shape({
      adminId: PropTypes.shape({
        cycleId: PropTypes.shape({
          id: PropTypes.number,
        }),
      }),
    }),
    testCenterCycleId: PropTypes.number,
    specialTestingFlag: PropTypes.bool,
  }).isRequired,
  room: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    roomType: PropTypes.object,
    loading: PropTypes.bool,
  }).isRequired,
  roomStatus: PropTypes.shape({
    status: PropTypes.string,
    loading: PropTypes.bool,
  }).isRequired,
  roomAvailableTasks: PropTypes.arrayOf(PropTypes.object).isRequired,
  completedTasks: PropTypes.arrayOf(PropTypes.object).isRequired,
  roomsDTO: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      manualTimerFlag: PropTypes.bool,
    })
  ).isRequired,
  irregularityTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      type: PropTypes.string,
    })
  ),
  displayTexts: PropTypes.shape({
    room_tips_for_success: PropTypes.shape({
      cycle: PropTypes.shape({
        id: PropTypes.number,
      }),
    }),
    room_close_instructions: PropTypes.shape({
      text: PropTypes.string,
    }),
  }),
  errorUpdateStatus: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),

  // Props from mapDispatchToProps
  actions: PropTypes.shape({
    getRoomStatus: PropTypes.func.isRequired,
    getRoom: PropTypes.func.isRequired,
    getTcEvent: PropTypes.func.isRequired,
    getTasks: PropTypes.func.isRequired,
    getIrregularityTypes: PropTypes.func.isRequired,
    getDisplayTexts: PropTypes.func.isRequired,
    getRoomsDTO: PropTypes.func.isRequired,
    getRoomDTO: PropTypes.func.isRequired,
    updateRoomStatus: PropTypes.func.isRequired,
    setRoomStatusAction: PropTypes.func.isRequired,
    setIrregularity: PropTypes.func.isRequired,
  }).isRequired,
};
